import React, { useEffect, useRef, useState } from "react";
import {
  Bot,
  X,
  Send,
  RotateCcw,
  MessageCircle,
  CheckCircle2,
  ArrowLeft,
} from "lucide-react";

import "./ConsultationBot.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||  "https://backendhub.co.in/bcservice"
).replace(/\/+$/, "");

const LEAD_API_URL = `${API_BASE_URL}/api/leads/`;

const WHATSAPP_NUMBER = "919432169467";

const PROJECT_OPTIONS = [
  {
    id: "WEBSITE",
    label: "Website Development",
    projectType: "Website Development",
  },
  {
    id: "ECOMMERCE",
    label: "Ecommerce Solution",
    projectType: "Ecommerce Development",
  },
  {
    id: "CRM",
    label: "CRM / MIS Software",
    projectType: "CRM / MIS Software",
  },
  {
    id: "MOBILE",
    label: "Mobile App",
    projectType: "Mobile App Development",
  },
  {
    id: "AI",
    label: "AI Solution",
    projectType: "AI Solution",
  },
  {
    id: "OTHER",
    label: "Something Else",
    projectType: "Other",
  },
];

const PROJECT_QUESTIONS = {
  WEBSITE: {
    question: "What kind of website are you planning?",
    options: [
      "Business Website",
      "Corporate Website",
      "Customer Portal",
      "Website Redesign",
      "Not Sure Yet",
    ],
  },

  ECOMMERCE: {
    question: "What type of ecommerce solution do you need?",
    options: [
      "New Online Store",
      "Existing Store Upgrade",
      "Marketplace",
      "Payment Integration",
      "Not Sure Yet",
    ],
  },

  CRM: {
    question: "What would you mainly like the software to manage?",
    options: [
      "Sales & Leads",
      "Customers",
      "Inventory",
      "Employees",
      "Reports / MIS",
      "Multiple Operations",
    ],
  },

  MOBILE: {
    question: "What kind of mobile application are you considering?",
    options: [
      "Customer App",
      "Employee App",
      "Sales App",
      "Service App",
      "Ecommerce App",
      "Not Sure Yet",
    ],
  },

  AI: {
    question: "Where would you like to use AI?",
    options: [
      "Business Automation",
      "AI Chatbot",
      "Document Processing",
      "Data Analysis",
      "AI Integration",
      "Need Guidance",
    ],
  },

  OTHER: {
    question: "Which option best describes your requirement?",
    options: [
      "Custom Software",
      "Cloud / Backend",
      "Dashboard",
      "Software Integration",
      "Technical Consultation",
      "Not Sure Yet",
    ],
  },
};

const BUDGET_OPTIONS = [
  "Below ₹50,000",
  "₹50,000 - ₹1,00,000",
  "₹1,00,000 - ₹3,00,000",
  "₹3,00,000+",
  "Need Consultation",
];

const TIMELINE_OPTIONS = [
  "Immediately",
  "Within 1 Month",
  "1 - 3 Months",
  "3+ Months",
  "Just Exploring",
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  companyName: "",
};

const createMessage = (sender, text) => ({
  id: `${Date.now()}-${Math.random()}`,
  sender,
  text,
});

function ConsultationBot() {
  const [isOpen, setIsOpen] = useState(false);

  const [step, setStep] = useState("PROJECT");

  const [messages, setMessages] = useState([
    createMessage(
      "bot",
      "Hi! I'm the Bairagi Consultants Project Assistant."
    ),
    createMessage(
      "bot",
      "I can help understand your requirement and arrange a free consultation."
    ),
    createMessage(
      "bot",
      "What are you looking to build?"
    ),
  ]);

  const [project, setProject] = useState(null);
  const [requirement, setRequirement] = useState("");
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");

  const [form, setForm] = useState(initialForm);

  const [inputValue, setInputValue] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [leadReference, setLeadReference] = useState("");

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages, step]);

  // Allows ContactPage or any other component to open the global bot:
  // window.dispatchEvent(new Event("open-consultation-bot"));
  useEffect(() => {
    const openBot = () => {
      setIsOpen(true);
    };

    window.addEventListener(
      "open-consultation-bot",
      openBot
    );

    return () => {
      window.removeEventListener(
        "open-consultation-bot",
        openBot
      );
    };
  }, []);

  const addMessage = (sender, text) => {
    setMessages((previous) => [
      ...previous,
      createMessage(sender, text),
    ]);
  };

  const handleProject = (option) => {
    setProject(option);

    addMessage("user", option.label);

    setTimeout(() => {
      addMessage(
        "bot",
        PROJECT_QUESTIONS[option.id].question
      );

      setStep("REQUIREMENT");
    }, 250);
  };

  const handleRequirement = (value) => {
    setRequirement(value);

    addMessage("user", value);

    setTimeout(() => {
      addMessage(
        "bot",
        "Great. What approximate budget are you considering?"
      );

      setStep("BUDGET");
    }, 250);
  };

  const handleBudget = (value) => {
    setBudget(value);

    addMessage("user", value);

    setTimeout(() => {
      addMessage(
        "bot",
        "And when would you ideally like to start?"
      );

      setStep("TIMELINE");
    }, 250);
  };

  const handleTimeline = (value) => {
    setTimeline(value);

    addMessage("user", value);

    setTimeout(() => {
      addMessage(
        "bot",
        "Perfect. Please tell me a little more about what you want to achieve."
      );

      setStep("DETAILS");
    }, 250);
  };

  const handleDetailsSubmit = (event) => {
    event.preventDefault();

    const value = inputValue.trim();

    if (!value) {
      return;
    }

    addMessage("user", value);

    setInputValue("");

    setTimeout(() => {
      addMessage(
        "bot",
        "Thanks. I just need your contact details so our team can get back to you."
      );

      setStep("CONTACT");
    }, 250);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const validateContact = () => {
  if (!form.fullName.trim()) {
    return "Please enter your name.";
  }

  if (!form.phone.trim()) {
    return "Please enter your phone number.";
  }

  const phoneValue = form.phone.trim();

  if (phoneValue.length < 6) {
    return "Phone number must contain at least 6 characters.";
  }

  if (!/^[0-9+\-\s()]+$/.test(phoneValue)) {
    return "Please enter a valid phone number.";
  }

  if (!form.email.trim()) {
    return "Please enter your email address.";
  }

  const emailPattern =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailPattern.test(form.email.trim())) {
    return "Please enter a valid email address.";
  }

  return "";
};

  const handleLeadSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateContact();

    if (validationError) {
      setSubmissionError(validationError);
      return;
    }

    setSubmissionError("");
    setIsSubmitting(true);

    const payload = {
      full_name: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),

      company_name:
        form.companyName.trim() || null,

      project_type:
        project?.projectType || "Other",

      budget_range:
        budget || null,

      timeline:
        timeline || null,

      project_details:
        `${requirement}. ${messages
          .filter(
            (message) =>
              message.sender === "user"
          )
          .map((message) => message.text)
          .join(" | ")}`,

      qualification: {
        consultation_type: requirement,
        bot_version: "V1",
      },

      acquisition: {
        source: "WEBSITE_BOT",
        page: window.location.pathname,
        entry_point: "consultation_bot",
      },

      lead_status: "NEW",
    };

    try {
      const response = await fetch(
        LEAD_API_URL,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

    if (!response.ok) {
        const validationMessage =
            data?.error?.details?.[0]?.message ||
            data?.error?.message ||
            data?.detail ||
            "Unable to submit consultation request.";

        throw new Error(validationMessage);
        }

      const reference =
        data?.reference ||
        data?.lead_reference ||
        data?.reference_no ||
        (data?.id
          ? `BC-${String(data.id).padStart(
              5,
              "0"
            )}`
          : "");

      setLeadReference(reference);

      addMessage(
        "bot",
        "Your consultation request has been received successfully."
      );

      setStep("SUCCESS");
    } catch (error) {
      console.error(
        "BOT lead submission failed:",
        error
      );

      setSubmissionError(
        error.message ||
          "Unable to submit your request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = [
      "Hi Bairagi Consultants,",
      "",
      "I would like to discuss a software project.",
      project
        ? `Project: ${project.label}`
        : "",
      requirement
        ? `Requirement: ${requirement}`
        : "",
      leadReference
        ? `Reference: ${leadReference}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    const url =
      `https://wa.me/${WHATSAPP_NUMBER}` +
      `?text=${encodeURIComponent(message)}`;

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const resetBot = () => {
    setStep("PROJECT");

    setProject(null);
    setRequirement("");
    setBudget("");
    setTimeline("");

    setForm(initialForm);

    setInputValue("");

    setSubmissionError("");
    setLeadReference("");

    setMessages([
      createMessage(
        "bot",
        "Hi! I'm the Bairagi Consultants Project Assistant."
      ),
      createMessage(
        "bot",
        "I can help understand your requirement and arrange a free consultation."
      ),
      createMessage(
        "bot",
        "What are you looking to build?"
      ),
    ]);
  };

  const goBack = () => {
    switch (step) {
      case "REQUIREMENT":
        setStep("PROJECT");
        break;

      case "BUDGET":
        setStep("REQUIREMENT");
        break;

      case "TIMELINE":
        setStep("BUDGET");
        break;

      case "DETAILS":
        setStep("TIMELINE");
        break;

      case "CONTACT":
        setStep("DETAILS");
        break;

      default:
        break;
    }
  };

  const renderOptions = (
    options,
    handler
  ) => (
    <div className="bc-bot-options">
      {options.map((option) => (
        <button
          key={
            typeof option === "string"
              ? option
              : option.id
          }
          type="button"
          className="bc-bot-option"
          onClick={() => handler(option)}
        >
          {typeof option === "string"
            ? option
            : option.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      {/* FLOATING BOT BUTTON */}

      <button
        type="button"
        className={`bc-bot-launcher ${
          isOpen ? "bc-bot-launcher-hidden" : ""
        }`}
        onClick={() => setIsOpen(true)}
        aria-label="Open consultation assistant"
      >
        <span className="bc-bot-launcher-pulse" />

        <Bot size={25} />

        <span className="bc-bot-launcher-label">
          Ask Us
        </span>
      </button>

      {/* BOT WINDOW */}

      {isOpen && (
        <section
          className="bc-bot-window"
          aria-label="Bairagi Consultants Project Assistant"
        >
          {/* HEADER */}

          <header className="bc-bot-header">
            <div className="bc-bot-header-icon">
              <Bot size={22} />
            </div>

            <div className="bc-bot-header-copy">
              <strong>
                Project Assistant
              </strong>

              <span>
                Bairagi Consultants
              </span>
            </div>

            <div className="bc-bot-header-actions">
              <button
                type="button"
                onClick={resetBot}
                title="Start again"
                aria-label="Start again"
              >
                <RotateCcw size={17} />
              </button>

              <button
                type="button"
                onClick={() =>
                  setIsOpen(false)
                }
                title="Close"
                aria-label="Close assistant"
              >
                <X size={20} />
              </button>
            </div>
          </header>

          {/* STATUS */}

          <div className="bc-bot-status">
            <span className="bc-bot-online-dot" />
            Online
            <span className="bc-bot-status-separator">
              •
            </span>
            Free consultation
          </div>

          {/* MESSAGES */}

          <div className="bc-bot-body">
            <div className="bc-bot-messages">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`bc-bot-message-row ${
                    message.sender ===
                    "user"
                      ? "bc-bot-message-user-row"
                      : ""
                  }`}
                >
                  {message.sender ===
                    "bot" && (
                    <div className="bc-bot-avatar">
                      <Bot size={15} />
                    </div>
                  )}

                  <div
                    className={`bc-bot-message ${
                      message.sender ===
                      "user"
                        ? "bc-bot-message-user"
                        : "bc-bot-message-assistant"
                    }`}
                  >
                    {message.text}
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* INTERACTION AREA */}

            <div className="bc-bot-interaction">
              {step !== "PROJECT" &&
                step !== "SUCCESS" && (
                  <button
                    type="button"
                    className="bc-bot-back"
                    onClick={goBack}
                  >
                    <ArrowLeft size={14} />
                    Back
                  </button>
                )}

              {step === "PROJECT" &&
                renderOptions(
                  PROJECT_OPTIONS,
                  handleProject
                )}

              {step === "REQUIREMENT" &&
                renderOptions(
                  PROJECT_QUESTIONS[
                    project.id
                  ].options,
                  handleRequirement
                )}

              {step === "BUDGET" &&
                renderOptions(
                  BUDGET_OPTIONS,
                  handleBudget
                )}

              {step === "TIMELINE" &&
                renderOptions(
                  TIMELINE_OPTIONS,
                  handleTimeline
                )}

              {step === "DETAILS" && (
                <form
                  className="bc-bot-text-form"
                  onSubmit={
                    handleDetailsSubmit
                  }
                >
                  <textarea
                    value={inputValue}
                    onChange={(event) =>
                      setInputValue(
                        event.target.value
                      )
                    }
                    placeholder="Example: We need software to manage customers, sales, payments and reports..."
                    rows="3"
                    maxLength="1000"
                    autoFocus
                  />

                  <button
                    type="submit"
                    disabled={
                      !inputValue.trim()
                    }
                  >
                    Continue
                    <Send size={16} />
                  </button>
                </form>
              )}

              {step === "CONTACT" && (
                <form
                  className="bc-bot-contact-form"
                  onSubmit={
                    handleLeadSubmit
                  }
                >
                  <div className="bc-bot-form-row">
                    <label>
                      Name *
                      <input
                        type="text"
                        name="fullName"
                        value={
                          form.fullName
                        }
                        onChange={
                          handleFormChange
                        }
                        placeholder="Your name"
                        autoComplete="name"
                      />
                    </label>

                    <label>
                      Phone *
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={
                          handleFormChange
                        }
                        placeholder="Mobile number"
                        autoComplete="tel"
                      />
                    </label>
                  </div>

                  <label>
                    Email *
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={
                        handleFormChange
                      }
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                  </label>

                  <label>
                    Company
                    <input
                      type="text"
                      name="companyName"
                      value={
                        form.companyName
                      }
                      onChange={
                        handleFormChange
                      }
                      placeholder="Optional"
                      autoComplete="organization"
                    />
                  </label>

                  {submissionError && (
                    <div className="bc-bot-error">
                      {submissionError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="bc-bot-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Submitting..."
                      : "Request Free Consultation"}

                    {!isSubmitting && (
                      <Send size={16} />
                    )}
                  </button>

                  <p className="bc-bot-privacy">
                    Your details are used only
                    to respond to your
                    consultation request.
                  </p>
                </form>
              )}

              {step === "SUCCESS" && (
                <div className="bc-bot-success">
                  <div className="bc-bot-success-icon">
                    <CheckCircle2
                      size={32}
                    />
                  </div>

                  <h3>
                    Request Received
                  </h3>

                  <p>
                    Thank you,{" "}
                    <strong>
                      {form.fullName}
                    </strong>
                    . Our team will review
                    your requirement and
                    contact you.
                  </p>

                  {leadReference && (
                    <div className="bc-bot-reference">
                      Consultation Reference
                      <strong>
                        {leadReference}
                      </strong>
                    </div>
                  )}

                  <button
                    type="button"
                    className="bc-bot-whatsapp"
                    onClick={openWhatsApp}
                  >
                    <MessageCircle
                      size={18}
                    />
                    Continue on WhatsApp
                  </button>

                  <button
                    type="button"
                    className="bc-bot-new-request"
                    onClick={resetBot}
                  >
                    Start Another Request
                  </button>
                </div>
              )}
            </div>
          </div>

          <footer className="bc-bot-footer">
            Bairagi Consultants • Software
            Solutions
          </footer>
        </section>
      )}
    </>
  );
}

export default ConsultationBot;